import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../../api/client'
import { useStores } from '../../app/StoreContext'
import type { RecipientStore, RecipientSummary } from '../../api/types'
import { Badge, Button, Card, Empty, ErrorBox, Field, Num, Spinner, formatDate } from '../../ui/primitives'
import { RecipientChart, RecipientTable } from './RecipientChart'

/**
 * Proposte per destinatario, con la finestra sulla data di previsione — la «data richiesta»
 * dell'applicazione attuale — e il dettaglio dei punti vendita che si apre cliccando una barra.
 *
 * <p>Il grafico e la tabella mostrano lo stesso dato: la tabella non è un ripiego, è la vista che
 * serve a chi stampa, a chi legge con uno screen reader e a chi deve copiare i numeri altrove.</p>
 */
export function RecipientPanel() {
  const { storeFilter, storesReady } = useStores()
  const navigate = useNavigate()

  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [view, setView] = useState<'chart' | 'table'>('chart')

  const [rows, setRows] = useState<RecipientSummary[]>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>()

  const [picked, setPicked] = useState<RecipientSummary>()
  const [detail, setDetail] = useState<RecipientStore[]>()
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState<unknown>()
  const detailRef = useRef<HTMLDivElement>(null)

  const query = { storeCode: storeFilter, from: from || undefined, to: to || undefined }

  const load = useCallback(() => {
    // Si aspetta l'elenco dei punti vendita: prima il filtro non sa se «tutti» siano tutti.
    if (!storesReady) return
    setLoading(true)
    setError(undefined)
    // Il dettaglio aperto si riferisce alla finestra precedente: chiuderlo evita di mostrare
    // numeri che non corrispondono più al grafico.
    setPicked(undefined)
    setDetail(undefined)

    api
      .get<RecipientSummary[]>('/dashboard/proposals-by-recipient', query)
      .then(setRows)
      .catch(setError)
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeFilter, storesReady, from, to])

  useEffect(load, [load])

  const open = useCallback(
    (recipient: RecipientSummary) => {
      setPicked(recipient)
      setDetail(undefined)
      setDetailError(undefined)
      setDetailLoading(true)

      api
        .get<RecipientStore[]>(
          `/dashboard/proposals-by-recipient/${encodeURIComponent(recipient.recipientCode)}`,
          query,
        )
        .then((d) => {
          setDetail(d)
          detailRef.current?.scrollIntoView({
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            block: 'nearest',
          })
        })
        .catch(setDetailError)
        .finally(() => setDetailLoading(false))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [storeFilter, from, to],
  )

  return (
    <>
      <Card
        title="Proposte per destinatario"
        actions={
          <div className="view-toggle" role="group" aria-label="Vista">
            <Button variant={view === 'chart' ? 'primary' : 'ghost'} onClick={() => setView('chart')}>
              Grafico
            </Button>
            <Button variant={view === 'table' ? 'primary' : 'ghost'} onClick={() => setView('table')}>
              Tabella
            </Button>
          </div>
        }
      >
        <div className="grid filter-grid">
          <Field label="Data richiesta da">
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="Data richiesta a">
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
          <div className="field field-actions">
            <Button
              variant="ghost"
              onClick={() => {
                setFrom('')
                setTo('')
              }}
              disabled={!from && !to}
            >
              Tutte le date
            </Button>
          </div>
        </div>

        {loading ?
          <Spinner />
        : error ?
          <ErrorBox error={error} onRetry={load} />
        : view === 'chart' ?
          <RecipientChart rows={rows ?? []} selected={picked?.recipientCode} onSelect={open} />
        : <RecipientTable rows={rows ?? []} onSelect={open} />}
      </Card>

      {picked && (
        <div ref={detailRef}>
          <Card
            title={
              <>
                Punti vendita — {picked.recipientName}{' '}
                <span className="muted">{picked.recipientCode}</span>
              </>
            }
            actions={
              <Button variant="ghost" onClick={() => setPicked(undefined)}>
                Chiudi
              </Button>
            }
          >
            {detailLoading ?
              <Spinner />
            : detailError ?
              <ErrorBox error={detailError} onRetry={() => open(picked)} />
            : !detail || detail.length === 0 ?
              <Empty>Nessun punto vendita per questo destinatario nel periodo.</Empty>
            : <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Punto vendita</th>
                      <th>Stato</th>
                      <th>Data richiesta</th>
                      <th className="num">Righe</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {detail.map((d) => (
                      <tr
                        key={d.proposalId}
                        className="clickable"
                        onClick={() => navigate(`/proposals/${d.proposalId}`)}
                      >
                        <td>{d.store}</td>
                        <td>
                          <Badge tone={d.confirmed ? 'ok' : 'warn'}>
                            {d.confirmed ? 'Confermata' : 'Da confermare'}
                          </Badge>
                        </td>
                        <td>{formatDate(d.forecastDate)}</td>
                        <td className="num">
                          <Num value={d.lineCount} />
                        </td>
                        <td className="num">
                          <span className="muted">Apri →</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            }
          </Card>
        </div>
      )}
    </>
  )
}
